<script setup lang="ts">
import ResultTablePL from '@/components/result/ResultTablePL.vue';
import ResultTablePlanet from '@/components/result/ResultTablePlanet.vue';

import type { DebrisResources, ResourceData, Resources } from '@/lib/calculator/result';
import { PartyMembers } from '@/lib/trashsim/party';
import type { Waves } from '@/lib/trashsim/waves';
import Format from '@/lib/trashsim/format';
import { computed, toRaw } from 'vue';
import ResourceOutput from '@/components/common/ResourceOutput.vue';

const props = defineProps<{
    modelValue: Waves
}>()

const emits = defineEmits<{
    (e: 'clear'): void
}>()

const waves = computed(() => props.modelValue)
// const clearWaves = () => emits('clear')

type TotalResult = {
    loses: ResourceData
    profits: ResourceData
    debris: {
        remaining: DebrisResources,
        recyclers: number
    },
    plunder: Resources
}

const total = computed(() => {
    let result: TotalResult = {
        debris: {
            remaining: { metal: 0, crystal: 0, deuterium: 0, total: 0 },
            recyclers: 0
        },
        loses: {
            attackers: { metal: 0, crystal: 0, deuterium: 0, total: 0 },
            defenders: { metal: 0, crystal: 0, deuterium: 0, total: 0 }
        },
        plunder: { metal: 0, crystal: 0, deuterium: 0, food: 0 },
        profits: {
            attackers: { metal: 0, crystal: 0, deuterium: 0, total: 0 },
            defenders: { metal: 0, crystal: 0, deuterium: 0, total: 0 }
        },
    };

    let allNull = true

    for (const wave of waves.value) {
        if (wave.result == null) {
            return result
        }

        allNull = false

        console.log("wave result", toRaw(wave.result))

        for (const party of PartyMembers) {
            result.loses[party].metal += wave.result.loses[party].metal
            result.loses[party].crystal += wave.result.loses[party].crystal
            result.loses[party].deuterium += wave.result.loses[party].deuterium
            result.loses[party].total += wave.result.loses[party].total

            result.profits[party].metal += wave.result.profits[party].metal
            result.profits[party].crystal += wave.result.profits[party].crystal
            result.profits[party].deuterium += wave.result.profits[party].deuterium
            result.profits[party].total += wave.result.profits[party].total
        }

        result.debris.remaining.metal += wave.result.debris.remaining.metal
        result.debris.remaining.crystal += wave.result.debris.remaining.crystal
        result.debris.remaining.deuterium += wave.result.debris.remaining.deuterium
        result.debris.remaining.total += wave.result.debris.remaining.total
        result.debris.recyclers += wave.result.debris.recyclers

        result.plunder.metal += wave.result.plunder.metal
        result.plunder.crystal += wave.result.plunder.crystal
        result.plunder.deuterium += wave.result.plunder.deuterium
        result.plunder.food += wave.result.plunder.food
    }

    if (allNull) {
        return null
    }

    return result
})

const sum = (res?: Resources): number | null => {
    if (res === undefined) { return null }
    return res.metal + res.crystal + res.deuterium + res.food
}

</script>

<template>
    <div id="results-total" v-if="waves.length > 1">
        <div id="results-total-title">
            <h3>{{ $t('home.resultstotal.title') }}</h3>
        </div>
        <div class="results-tables">
            <ResultTablePL
                v-for="party of PartyMembers"
                :party="party"
                :profits="total?.profits[party]"
                :loses="total?.loses[party]"
            />

            <ResultTablePlanet :debris="total?.debris" />

            <div class="result-table-plunder result-table">
                <table class="resource-table">
                    <thead><tr><th colspan="2">Plunder Captured</th></tr></thead>
                    <tbody>
                    <tr v-for="resource in ['metal', 'crystal', 'deuterium', 'food'].map(v => v as keyof Resources)">
                        <td><div class="resource" :class="'resource-'+resource"></div></td>
                        <td>{{ Format.Number(total?.plunder[resource]) }}</td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr class="total">
                        <td><div class="fixed resource-total">=</div></td>
                        <td>{{ Format.Number(sum(total?.plunder)) }}</td>
                    </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>

<style scoped>
.results-tables .result-table-plunder tr td:nth-child(5) {
    text-align: right;
}
.results-tables .result-table-plunder tr td:nth-child(6) {
    text-align: left;
}

#results-total {
    margin-top: 25px;
    order: 5;
    flex: 1 0 100%;
}
#results-total-title {
    padding: 15px 20px;
    background: rgba(30, 43, 57, 0.9);
    border-top-right-radius: 3px;
    border-top-left-radius: 3px;
    display: inline-block;
    margin-bottom: -1px;
}
#results-total-title h3 {
    margin: 0;
    font-size: 2rem;
}
#results-total .result-table:nth-child(1), #results-total .result-table:nth-child(2) {
    flex: 1 0 49%;
    order: 1;
}
#results-total .result-table:nth-child(3) {
    flex: 1 0 60%;
    order: 3;
    margin-left: 0;
}
#results-total .result-table:nth-child(4) {
    flex: 1 0 38%;
    order: 4;
}
#results-total .result-table:nth-child(3), #results-total .result-table:nth-child(4) {
    margin-top: 1%;
}

@media (min-width: 84em) {
    #results-total .result-table + .result-table { margin: 0 0 0 0.5%; }
	#results-total .result-table:nth-child(1), #results-total .result-table:nth-child(2) {
        flex: 1 0 29%;
    }
	#results-total .result-table:nth-child(3) {
        flex: 1 0 25%;
    }
	#results-total .result-table:nth-child(4) {
        flex: 1 0 14%;
    }
}

@media (max-width: 50em) {
    #results-total .result-table:nth-child(1), #results-total .result-table:nth-child(2) {
        flex-basis: 100%;
        margin: 0 0 10px;
    }
	#results-total .result-table:nth-child(3), #results-total .result-table:nth-child(4) {
        margin-top: 0;
    }
}

@media (max-width: 37em) {
    #results-total .result-table:nth-child(3), #results-total .result-table:nth-child(4) {
        flex-basis: 100%;
        margin: 0 0 10px;
    }
}
</style>